// src/lib/accueil-incident.js — « QUI PAIE CET INCIDENT ? » : le calcul.
//
// ⚠️ MODULE PUR — la feuille rend, ce module décide. Spec Dimitri 2026-08-26
// (amendée sur mes quatre réponses) : ce n'est pas l'incident qui est à
// trancher — l'agent a déjà jugé le linge irrécupérable — c'est QUI PAIE.
//
// ⚠️ PAS DE JARGON : jamais « qualification », « état financier » ni
// « refacturation » à l'écran. Les libellés canoniques du code
// (« Refacturé propriétaire »…) restent des VALEURS, pas des libellés.
//
// ⚠️ LES BOUTONS S'ADAPTENT AU LOGEMENT : sur une chambre de la Maison Zen,
// FBZ est l'hôte — « Le propriétaire le paie » serait un bouton qui ment.
// L'absence est EXPLIQUÉE, pas grisée.

/** Les valeurs canoniques du code (domain-constants) — dupliquées à dessein,
 *  ce module reste sans dépendance (pattern incidents-helpers). */
export const ETAT = Object.freeze({
  SANS_COUT: 'Sans coût',
  REFACTURE_PROPRIO: 'Refacturé propriétaire',
  ACHAT_FBZ: 'Achat FULLBNBZEN',
  REMBOURSE_AIRCOVER: 'Remboursé AirCover',
  AIRCOVER_REFACTURE: 'AirCover + refacturé',
})

/** Les articles d'un incident qui attendent ENCORE la décision. */
export function articlesATrancher(inc) {
  const degs = Array.isArray(inc && inc.degradations) ? inc.degradations : []
  return degs.filter((d) => d && d.statutMenage === 'Non récupérable'
    && !d.etatFinancier && d.traitement == null)
}

/**
 * Les choix offerts, selon le logement. Chaque choix porte son libellé ET sa
 * conséquence écrite — la phrase EXACTE de la spec (celle de la facture dit
 * la vérité : la ligne part au mois du REMPLACEMENT, pas de la décision).
 */
export function choixPourLogement({ sousLoc }) {
  if (sousLoc) {
    return {
      // ⚠️ L'ABSENCE EXPLIQUÉE (spec) — mieux qu'un bouton grisé.
      explication: "Vous êtes l'hôte de ce logement — il n'y a personne à qui refacturer.",
      choix: [
        { id: 'aircover', libelle: 'Airbnb rembourse',
          consequence: "une demande AirCover reste à déposer — c'est FBZ qui perçoit" },
        { id: 'nous', libelle: "C'est pour nous",
          consequence: 'la dépense entre dans vos charges du mois' },
      ],
    }
  }
  return {
    explication: null,
    choix: [
      { id: 'proprio', libelle: 'Le propriétaire le paie',
        consequence: "la ligne partira sur sa facture du mois où l'article sera remplacé" },
      { id: 'aircover', libelle: 'Airbnb rembourse',
        consequence: 'une demande AirCover reste à déposer, vous seul pouvez le faire' },
      { id: 'nous', libelle: "C'est pour nous",
        consequence: 'la dépense entre dans vos charges du mois' },
    ],
  }
}

/**
 * Le patch à écrire sur la dégradation, selon le choix.
 * `remplacement` = la sous-question du cas AirCover hors sous-loc
 * (« et le remplacement, qui le paie ? » : 'proprio' | 'nous').
 * ⚠️ Sur la sous-loc, pas de sous-question : c'est forcément nous — et le
 * marqueur `aircoverDemande` porte « une demande reste à déposer » (le modèle
 * réel n'a pas d'état « demande en attente » : les montants aircoverMontant /
 * aircoverFbz n'existent qu'une fois la réponse SAISIE).
 */
export function patchPourChoix({ choix, remplacement, sousLoc, montant }) {
  const cout = Number.isFinite(montant) && montant > 0 ? String(montant) : null
  if (choix === 'sans-cout') return { etatFinancier: ETAT.SANS_COUT }
  if (choix === 'nous') return { etatFinancier: ETAT.ACHAT_FBZ, ...(cout ? { cout } : {}) }
  if (choix === 'proprio') return { etatFinancier: ETAT.REFACTURE_PROPRIO, ...(cout ? { cout } : {}) }
  if (choix === 'aircover') {
    if (sousLoc) {
      return { etatFinancier: ETAT.ACHAT_FBZ, aircoverDemande: true, ...(cout ? { cout } : {}) }
    }
    return {
      etatFinancier: remplacement === 'proprio' ? ETAT.AIRCOVER_REFACTURE : ETAT.ACHAT_FBZ,
      aircoverDemande: true,
      ...(cout ? { cout } : {}),
    }
  }
  return null
}

/** « 34 € » — entier si rond, virgule sinon. */
export function eurosCourt(montant) {
  if (!Number.isFinite(montant)) return ''
  return `${montant % 1 ? montant.toFixed(2).replace('.', ',') : montant} €`
}

/**
 * LA RÉCAPITULATION — la phrase bleue avant de valider. Elle dit la vérité :
 * un mois n'est nommé QUE si le remplacement est daté.
 */
export function phraseRecap({ article, montant, choix, remplacement, sousLoc, proprioNom, remplaceLe }) {
  const m = eurosCourt(montant)
  const tete = [article, m].filter(Boolean).join(', ')
  if (choix === 'sans-cout') return `${article} — sans conséquence financière.`
  if (choix === 'nous') return `${tete}, dans vos charges du mois.`
  if (choix === 'proprio') {
    const quand = quandRemplacement(remplaceLe)
    return `${tete}, refacturé à ${proprioNom || 'son propriétaire'} ${quand}.`
  }
  if (choix === 'aircover') {
    if (sousLoc) return `${tete} — demande AirCover à déposer, remboursement perçu par FBZ.`
    if (remplacement === 'proprio') {
      return `${tete} — demande AirCover à déposer, et le remplacement refacturé à ${proprioNom || 'son propriétaire'} ${quandRemplacement(remplaceLe)}.`
    }
    return `${tete} — demande AirCover à déposer, le remplacement est pour vous.`
  }
  return ''
}

const MOIS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet',
  'août', 'septembre', 'octobre', 'novembre', 'décembre']

function quandRemplacement(remplaceLe) {
  const m = /^(\d{4})-(\d{2})/.exec(String(remplaceLe || ''))
  if (!m) return 'à son remplacement'
  return `sur la facture de ${MOIS_FR[Number(m[2]) - 1]}`
}

// ── L'ÉCHÉANCE AIRCOVER — calculée au rendu, jamais stockée ─────────────────
//
// La règle Airbnb : la demande part sous 14 JOURS après le départ du voyageur,
// OU avant l'arrivée du suivant — au premier des deux. Ambre sous 3 jours,
// terracotta si dépassé — et DÉPASSÉ NE VEUT PAS DIRE EFFACÉ : on écrit
// « délai Airbnb dépassé », on ne cache pas.

export function echeanceAircover({ departVoyageur, prochaineArrivee, prochainVoyageur, todayIso }) {
  const depart = jourValide(departVoyageur)
  if (!depart) return null
  const quatorze = decaler(depart, 14)
  const arrivee = jourValide(prochaineArrivee)
  const limite = arrivee && arrivee < quatorze ? arrivee : quatorze
  const cause = arrivee && arrivee < quatorze
    ? `${prochainVoyageur || 'le voyageur suivant'} arrive le ${jourCourtFr(arrivee)}`
    : 'règle des 14 jours Airbnb'
  if (todayIso > limite) {
    return { ton: 'terra', limite, phrase: `Délai Airbnb dépassé (c'était avant le ${jourCourtFr(limite)}).` }
  }
  const joursRestants = joursEntre(todayIso, limite)
  return {
    ton: joursRestants < 3 ? 'ambre' : 'neutre',
    limite,
    phrase: `À déposer avant le ${jourCourtFr(limite)} — ${cause}.`,
  }
}

function jourValide(iso) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(iso || '')) ? String(iso) : null
}
function decaler(iso, jours) {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d + jours)).toISOString().slice(0, 10)
}
function joursEntre(a, b) {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400000)
}
function jourCourtFr(iso) {
  const [, m, d] = iso.split('-').map(Number)
  return `${d === 1 ? '1er' : d} ${MOIS_FR[m - 1]}`
}

/**
 * « Une réponse AirCover à saisir », VERSION RÉELLE. Le modèle n'a pas d'état
 * « attente » : la demande est OUVERTE quand la décision l'a marquée
 * (`aircoverDemande`) ou que l'état vaut AirCover, ET qu'aucun remboursement
 * n'est encore saisi (aircoverMontant côté proprio, aircoverFbz côté FBZ).
 */
export function aircoverASaisirDeg(d, { sousLoc } = {}) {
  if (!d) return false
  if (d.aircoverRefuse === true) return false // Airbnb a dit non — saisi, clos.
  const decide = d.aircoverDemande === true
    || d.etatFinancier === ETAT.REMBOURSE_AIRCOVER
    || d.etatFinancier === ETAT.AIRCOVER_REFACTURE
  if (!decide) return false
  const percu = sousLoc ? parseFloat(d.aircoverFbz) > 0 : parseFloat(d.aircoverMontant) > 0
  return !percu
}
